import {
  InputMaybe,
  JobDurationInput,
  type JobPostingBudgetInterval,
} from '#app/__generated__/api.js';
import { getBudgetAbbreviation } from '#app/utilities/getBudgetAbbreviation.js';
import { formatCurrency } from '#app/utilities/money.js';
import {
  buildJobDurationDisplay,
  getMaybeOpportunityDuration,
} from '@contra/feature-job-postings';

const formatEstimatedHours = (hours?: null | number) => {
  return hours ? (hours > 1 ? `${hours} hrs/wk` : `${hours} hr/wk`) : null;
};

export const getBudgetLabelFromBudgetRange = (budget: {
  feeMax?: null | number;
  feeMin?: null | number;
}) => {
  let budgetLabel: null | string;

  if (budget?.feeMin && budget?.feeMax && budget?.feeMin !== budget?.feeMax) {
    budgetLabel = `${formatCurrency(budget.feeMin, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    })} - ${formatCurrency(budget.feeMax, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    })}`;
  } else if (budget?.feeMin) {
    budgetLabel = formatCurrency(budget.feeMin, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  } else {
    budgetLabel = '';
  }

  return budgetLabel;
};

export const getWorkDetailsLabel = (details: {
  budget: null | {
    estimatedHours: null | number;
    feeMax?: null | number;
    feeMin?: null | number;
    interval: JobPostingBudgetInterval | null;
  };
  maxDuration: null | number;
  minDuration: null | number;
}) => {
  const { budget, maxDuration, minDuration } = details;
  const duration = getMaybeOpportunityDuration({ maxDuration, minDuration });

  const intervalRate = budget?.interval
    ? '/' + getBudgetAbbreviation(budget.interval)
    : '';

  const workDetailsLabel = [
    `${getBudgetLabelFromBudgetRange({
      feeMax: budget?.feeMax ?? null,
      feeMin: budget?.feeMin ?? null,
    })}${intervalRate}`,
    duration?.title ? duration.title : null,
    budget?.estimatedHours && `${budget.estimatedHours} hours / week`,
  ]
    .filter(Boolean)
    .join(' • ');

  return workDetailsLabel;
};

export const getWorkDetails = ({
  duration,
  estimatedHours,
  feeMax,
  feeMin,
  interval,
  maxDuration,
  minDuration,
  title,
}: {
  duration?: Partial<InputMaybe<JobDurationInput>>;
  estimatedHours?: null | number;
  feeMax?: null | number;
  feeMin?: null | number;
  interval?: JobPostingBudgetInterval | null;
  maxDuration?: null | number;
  minDuration?: null | number;
  startTimeframe?: null | string;
  title?: null | string;
}) => {
  const legacyDuration = getMaybeOpportunityDuration({
    maxDuration: maxDuration ?? null,
    minDuration: minDuration ?? null,
  });

  const intervalRate = interval ? `/${getBudgetAbbreviation(interval)}` : '';

  const jobDuration = buildJobDurationDisplay({
    amount: duration?.amount ?? null,
    interval: duration?.interval ?? null,
  });

  const durationDetails = [
    ...(legacyDuration ? [legacyDuration?.title] : [jobDuration]),
    formatEstimatedHours(estimatedHours),
  ]
    .filter(Boolean)
    .join(' • ');

  const rate = feeMin
    ? `${getBudgetLabelFromBudgetRange({
        feeMax: feeMax ?? undefined,
        feeMin,
      })}${feeMax ? '' : '+'}${intervalRate}`
    : null;

  return { durationDetails, rate, title };
};
